/**
 * TrainingSidebar — the grouped desktop sidebar for the Training section, a 1:1
 * port of the CA CDL trainer design's sidebar: `.sidebar` (360px, collapses to
 * 60px with a `.side-toggle`), a `.brand` row, `.nav-sec` groups of `.nav-btn`
 * links with `.nav-badge` counts, and the `.side-foot` DMV disclaimer. Styling is
 * the vendored design CSS (training-design.css, scoped under `.cdl-scope`).
 * Sections + badges come from `trainingNav` so the shell can't drift from the
 * curriculum.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  TRAINING_NAV_SECTIONS,
  navBadge,
  ZERO_PROGRESS,
  type TrainingProgressCounts,
} from '@/pages/training/trainingNav';

interface TrainingSidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
  /** Progress-derived badge counts; zero until the TR-5 store lands. */
  progress?: TrainingProgressCounts;
}

export function TrainingSidebar({
  collapsed,
  onToggle,
  progress = ZERO_PROGRESS,
}: TrainingSidebarProps) {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = !collapsed || isHovered;

  return (
    <aside
      className={cn('sidebar', !isExpanded && 'collapsed')}
      onMouseEnter={() => collapsed && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Collapse toggle (.side-toggle rotates its chevron when collapsed) */}
      <button
        type="button"
        className="side-toggle"
        onClick={() => onToggle(!collapsed)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronLeft />
      </button>

      {/* Brand row */}
      <div className="brand">
        <GraduationCap style={{ width: 22, height: 22, flexShrink: 0, color: '#8FB560' }} />
        {isExpanded && <span className="sub">CA CDL Class A · Trainer</span>}
      </div>

      <div id="nav">
        {TRAINING_NAV_SECTIONS.map((section) => (
          <div className="nav-sec" key={section.title}>
            <h3>{section.title}</h3>
            {section.items.map((item) => {
              const isActive = location.pathname === item.path;
              const badge = navBadge(item.key, progress);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn('nav-btn', isActive && 'active')}
                  title={item.name}
                >
                  <Icon />
                  <span>{item.name}</span>
                  {badge && <span className={cn('nav-badge', badge.alert && 'alert')}>{badge.text}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* DMV disclaimer — required standing copy (spec §disclaimer). */}
      <div className="side-foot">
        Always verify current rules at{' '}
        <a href="https://www.dmv.ca.gov" target="_blank" rel="noopener noreferrer">
          dmv.ca.gov
        </a>
        .
      </div>
    </aside>
  );
}
